import React, { useRef, useState } from 'react';
import { Button, Alert } from 'reactstrap';
import { UploadCloud, Download, FileText } from 'react-feather';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import { CSV_HEADER_ALIASES, EMPTY_ROW, SAMPLE_CSV, INPUT_COLUMNS } from '../searchScrape.constants';
import { buildQuery } from '../searchScrape.queryBuilder';

// Maps a raw header cell to a canonical row key (or null if unrecognized).
function canonicalKey(header) {
  const norm = String(header || '').trim().toLowerCase();
  return CSV_HEADER_ALIASES[norm] || null;
}

// Parses a sheet (array-of-arrays) into canonical row objects.
function parseRows(aoa) {
  if (!aoa.length) return [];
  const headers = aoa[0].map(canonicalKey);
  const rows = [];
  for (let i = 1; i < aoa.length; i += 1) {
    const raw = aoa[i];
    if (!raw || raw.every((c) => c == null || String(c).trim() === '')) continue;
    const row = { ...EMPTY_ROW };
    headers.forEach((key, col) => {
      if (key && raw[col] != null) row[key] = String(raw[col]).trim();
    });
    rows.push(row);
  }
  return rows;
}

export default function CsvUpload({ onParsed, disabled }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parsedCount, setParsedCount] = useState(0);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
        const rows = parseRows(aoa);
        if (!rows.length) {
          toast.error('No data rows found. Check the file has a header row and at least one entry.');
          return;
        }
        const valid = rows.filter((r) => buildQuery(r));
        if (!valid.length) {
          toast.error('Rows parsed but every query is empty — check your column headers.');
          return;
        }
        setFileName(file.name);
        setParsedCount(valid.length);
        onParsed(valid);
        toast.success(`Loaded ${valid.length} row(s) from ${file.name}`);
      } catch (err) {
        toast.error(`Failed to parse file: ${err.message}`);
      }
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'search_scrape_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div
        className="border rounded p-4 text-center"
        style={{ borderStyle: 'dashed', background: '#fafbfc', cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (!disabled) handleFile(e.dataTransfer.files?.[0]);
        }}
      >
        <UploadCloud size={32} className="text-primary mb-2" />
        <div className="fw-bold">Drag & drop a CSV / XLSX file, or click to browse</div>
        <div className="text-muted small mt-1">
          Recognized columns: {INPUT_COLUMNS.map((c) => c.label).join(', ')} (Domain → Site)
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="d-none"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled}
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <Button color="link" className="p-0" onClick={downloadTemplate}>
          <Download size={15} className="me-1" />
          Download CSV template
        </Button>
        {fileName && (
          <span className="text-success small">
            <FileText size={14} className="me-1" />
            {fileName} — {parsedCount} row(s) ready
          </span>
        )}
      </div>

      {parsedCount > 0 && (
        <Alert color="info" className="mt-3 mb-0 py-2">
          CSV uploads run as a background job — you can watch progress and results stream in below.
        </Alert>
      )}
    </div>
  );
}
